import React, { useState, useEffect } from 'react';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Upload, 
  X, 
  Settings
} from 'lucide-react';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import ResponsiveText from '../ui/ResponsiveText';
import ResponsiveTable from '../ui/ResponsiveTable';
import { useGetRequirementValues, useCreateRequirementValue, useUpdateRequirementValue, useDeleteRequirementValue } from '../../lib/hooks/useApi';

interface RequirementValue {
  id: string;
  category: string;
  value: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  metadata?: Record<string, any>;
  created_by: string;
  created_at: string;
  updated_at: string;
}



export const RequirementsValueManagement: React.FC = () => {
  const { confirm } = useConfirmation();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<RequirementValue | null>(null);
  const [formData, setFormData] = useState({
    value: '',
    display_name: '',
    description: '',
    sort_order: 0,
    metadata: {}
  });

  const { data: categories, isLoading } = useGetRequirementValues();
  const createValue = useCreateRequirementValue();
  const updateValue = useUpdateRequirementValue();
  const deleteValue = useDeleteRequirementValue();

  // Available categories
  const availableCategories = [
    'seniority',
    'experience_level',
    'employment_type',
    'currency',
    'degree',
    'field_of_study',
    'certifications',
    'benefits',
    'skills',
    'locations',
    'status'
  ];

  useEffect(() => {
    if (availableCategories.length > 0 && !selectedCategory) {
      setSelectedCategory(availableCategories[0]);
    }
  }, [availableCategories]);

  const handleCreateValue = async () => {
    try {
      await createValue.mutateAsync({
        category: selectedCategory,
        ...formData
      });
      setIsCreateModalOpen(false);
      setFormData({ value: '', display_name: '', description: '', sort_order: 0, metadata: {} });
    } catch (error) {
      console.error('Error creating value:', error);
    }
  };

  const handleUpdateValue = async () => {
    if (!editingValue) return;
    
    try {
      await updateValue.mutateAsync({
        category: selectedCategory,
        value_id: editingValue.id,
        ...formData
      });
      setIsEditModalOpen(false);
      setEditingValue(null);
      setFormData({ value: '', display_name: '', description: '', sort_order: 0, metadata: {} });
    } catch (error) {
      console.error('Error updating value:', error);
    }
  };

  const handleDeleteValue = async (valueId: string) => {
    confirm({
      title: 'Delete Value',
      description: 'Are you sure you want to delete this value?',
      variant: 'destructive',
      confirmText: 'Delete',
      onConfirm: async () => {
    
        try {
          await deleteValue.mutateAsync({
            category: selectedCategory,
            value_id: valueId
          });
        } catch (error) {
          console.error('Error deleting value:', error);
        }
      }
    });
  };

  const handleEditValue = (value: RequirementValue) => {
    setEditingValue(value);
    setFormData({
      value: value.value,
      display_name: value.display_name,
      description: value.description || '',
      sort_order: value.sort_order,
      metadata: value.metadata || {}
    });
    setIsEditModalOpen(true);
  };

  const getCategoryDisplayName = (category: string) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'seniority': return '👨‍💼';
      case 'experience_level': return '📊';
      case 'employment_type': return '💼';
      case 'currency': return '💰';
      case 'degree': return '🎓';
      case 'field_of_study': return '📚';
      case 'certifications': return '🏆';
      case 'benefits': return '🎁';
      case 'skills': return '⚡';
      case 'locations': return '📍';
      case 'status': return '📋';
      default: return '📝';
    }
  };

  const filteredValues = categories?.categories?.find((cat: any) => cat.category === selectedCategory)?.values?.filter((value: any) =>
    value.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    value.value.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <ResponsiveContainer size="full" maxWidth="7xl">
      <div className="glass-card">
        {/* Header */}
        <div className="px-6 py-4 border-b border-glass-border">
          <div className="flex items-center justify-between">
            <div>
              <ResponsiveText variant="heading-lg" weight="bold" className="text-text-enterprise-primary">
                Requirements Value Management
              </ResponsiveText>
              <ResponsiveText variant="body-sm" className="text-text-enterprise-secondary mt-1">
                Manage dropdown values for requirement forms
              </ResponsiveText>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsImportModalOpen(true)}
                className="px-4 py-2 glass-button text-text-enterprise-secondary hover:text-text-enterprise-primary hover:shadow-glow-primary flex items-center gap-2 transition-all duration-300"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-4 py-2 glass-button bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary hover:shadow-glow-primary flex items-center gap-2 transition-all duration-300"
              >
                <Plus className="w-4 h-4" />
                Add Value
              </button>
            </div>
          </div>
        </div>

        {/* Category Selection */}
        <div className="px-6 py-4 border-b border-glass-border glass-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <ResponsiveText variant="body-sm" weight="medium" className="text-text-enterprise-primary">
              Category:
            </ResponsiveText>
            
            {/* Desktop: Responsive Grid Layout */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 flex-1">
              {availableCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 backdrop-blur-md ${
                    selectedCategory === category
                      ? 'bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 border border-primary-500/40 text-text-enterprise-primary shadow-glow-primary'
                      : 'bg-gradient-to-br from-glass-surface/30 via-glass-surface/50 to-glass-surface/70 border border-glass-border/20 text-text-enterprise-secondary hover:bg-gradient-to-br hover:from-primary-500/15 hover:via-primary-600/25 hover:to-secondary-500/15 hover:border-primary-500/30 hover:text-text-enterprise-primary hover:shadow-glow-primary hover:-translate-y-0.5'
                  }`}
                >
                  <span className="mr-2">{getCategoryIcon(category)}</span>
                  {getCategoryDisplayName(category)}
                </button>
              ))}
            </div>
            
            {/* Mobile: Dropdown Selection */}
            <div className="md:hidden w-full">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 rounded-lg glass-input text-text-enterprise-primary border-glass-border focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-300"
              >
                {availableCategories.map((category) => (
                  <option key={category} value={category}>
                    {getCategoryIcon(category)} {getCategoryDisplayName(category)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-6 py-4 border-b border-glass-border">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary w-4 h-4" />
                <input
                  type="text"
                  placeholder={`Search ${getCategoryDisplayName(selectedCategory)} values...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 glass-input text-text-enterprise-primary border-glass-border focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-300"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-enterprise-secondary">
                {filteredValues.length} values
              </span>
            </div>
          </div>
        </div>

        {/* Values Table */}
        <div className="overflow-x-auto">
          <ResponsiveTable>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Sort Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-enterprise-tertiary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredValues.map((value: any) => (
                  <tr key={value.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {value.value}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {value.display_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-text-enterprise-tertiary max-w-xs truncate">
                      {value.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-enterprise-tertiary">
                      {value.sort_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        value.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {value.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditValue(value)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteValue(value.id)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        </div>

        {/* Empty State */}
        {filteredValues.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Settings className="w-12 h-12 mx-auto" />
            </div>
            <ResponsiveText variant="heading-md" weight="medium" className="text-gray-900 mb-2">
              No values found
            </ResponsiveText>
            <ResponsiveText variant="body-sm" className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first value'}
            </ResponsiveText>
            {!searchTerm && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add First Value
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                              <ResponsiveText variant="heading-md" weight="bold" className="text-gray-900">
                Add New Value
              </ResponsiveText>
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value *
                </label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., senior"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Senior Level"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Optional description..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateValue}
                disabled={!formData.value || !formData.display_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingValue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                              <ResponsiveText variant="heading-md" weight="bold" className="text-gray-900">
                Edit Value
              </ResponsiveText>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value *
                </label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateValue}
                disabled={!formData.value || !formData.display_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                              <ResponsiveText variant="heading-md" weight="bold" className="text-gray-900">
                Import Default Values
              </ResponsiveText>
                <button
                  onClick={() => setIsImportModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <ResponsiveText variant="body-sm" className="text-gray-600 mb-4">
                Import pre-defined values for the selected category. This will add common options that are typically used.
              </ResponsiveText>
              
              <div className="space-y-3">
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      // TODO: Implement import functionality
                      confirm({
                        title: 'Coming Soon',
                        description: `Import functionality for ${category} will be implemented`,
                        variant: 'info',
                        confirmText: 'OK',
                        onConfirm: () => {}
                      });
                      setIsImportModalOpen(false);
                    }}
                    className="w-full p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getCategoryIcon(category)}</span>
                      <div>
                        <div className="font-medium text-gray-900">
                          {getCategoryDisplayName(category)}
                        </div>
                        <div className="text-sm text-text-enterprise-tertiary">
                          Import common {category} values
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveContainer>
  );
};
